// src/constants/endpoints.ts

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string
const AUTH_PREFIX = `/auth`

export const endpoints = {
  auth: {
    login: `${BASE_URL}${AUTH_PREFIX}/login`,
    signup: `${BASE_URL}${AUTH_PREFIX}/signup`,
    logout: `${BASE_URL}${AUTH_PREFIX}/logout`,
    refresh: `${BASE_URL}${AUTH_PREFIX}/refresh`,

    forgotPassword: {
      requestOtp: `${BASE_URL}${AUTH_PREFIX}/forgot-password/request`,
      verifyOtp: `${BASE_URL}${AUTH_PREFIX}/forgot-password/verify`,
      setNewPassword: `${BASE_URL}${AUTH_PREFIX}/forgot-password/set-new`,
    },
  },
  user: {
    profile: `${BASE_URL}/user/profile`,
    role: `${BASE_URL}/role`,
    statistics: `${BASE_URL}/super-admin/users/statistics`,
  },
  superAdmin: {
    suspendUser: (id: string) => `${BASE_URL}/super-admin/users/${id}/suspend`,
    unsuspendUser: (id: string) =>
      `${BASE_URL}/super-admin/users/${id}/unsuspend`,
    blockUser: (id: string) => `${BASE_URL}/super-admin/users/${id}/suspend`,
    unblockUser: (id: string) =>
      `${BASE_URL}/super-admin/users/${id}/unsuspend`,
    createUser: `${BASE_URL}/super-admin/user`,
    editUser: (id: string) => `${BASE_URL}/super-admin/user/${id}`,
    getUserById: (id: string) => `${BASE_URL}/super-admin/users/${id}`,
    getUserStatistics: `${BASE_URL}/super-admin/users/statistics`,
    getUserActivity: (id: string) => `${BASE_URL}/userActivity/${id}`,
    event: `${BASE_URL}/super-admin/events`,
    getExportUser: `${BASE_URL}/super-admin/users/export`,
    suspendBulkUser: `${BASE_URL}/super-admin/users/bulk/suspend`,
    assignRoleBulkUser: `${BASE_URL}/super-admin/users/bulk/assign-role`,
    getUserAttendedEvents: (id: string) =>
      `${BASE_URL}/super-admin/users/attended-events/${id}`,
    setMessageAdmin: (id: string) => `/super-admin/chat-admin/${id}`,
  },
  upload: {
    getUploadUrl: `${BASE_URL}/upload/upload-url`,
    getDownloadUrl: `${BASE_URL}/upload/download-url`,
  },
  event: {
    event: `${BASE_URL}/events`,
    getEventById: (id: string) => `${BASE_URL}/super-admin/events/${id}`,
    editEvent: (id: string) => `${BASE_URL}/events/${id}`,
    category: `${BASE_URL}/category`,
    updateEventStatus: (id: string) => `${BASE_URL}/events/status/${id}`,
    updateEventAcknowledge: (id: string) =>
      `${BASE_URL}/events/acknowledge/${id}`,
    getExportEvent: `${BASE_URL}/events/export`,
    getExportPromoCodeUsage: `${BASE_URL}/events/promo-code-usage/export`,
    getEventStatistics: `${BASE_URL}/events/statistics`,
    tag: `${BASE_URL}/tag`,
    initiateRefund: (transactionId: string) =>
      `${BASE_URL}/events/booking/initiate-refund/${transactionId}`,
    assignSponsorToEvent: `${BASE_URL}/events/assign-sponsor`,
    getAssignedSponsors: (eventId: string) =>
      `${BASE_URL}/events/assigned-sponsors/${eventId}`,
    updatePlatformFees: `${BASE_URL}/events/event/processing-fees`,
    getEventDates: (id: string) => `${BASE_URL}/events/event-date/${id}`,
    getPaymentStatus: (transactionId: string) =>
      `${BASE_URL}/events/verify-payment/${transactionId}`,
    updatePaymentStatus: (transactionId: string) =>
      `${BASE_URL}/events/process-payment/${transactionId}`,
  },
  category: {
    getCategory: `${BASE_URL}/category/list`,
    createCategory: `${BASE_URL}/category`,
    updateCategory: (id: string) => `${BASE_URL}/category/${id}`,
    deleteCategory: (id: string) => `${BASE_URL}/category/${id}`,
  },
  tag: {
    getTags: `${BASE_URL}/tag/list`,
    createTag: `${BASE_URL}/tag`,
    updateTag: (id: string) => `${BASE_URL}/tag/${id}`,
    deleteTag: (id: string) => `${BASE_URL}/tag/${id}`,
  },
  attendee: {
    getAttendees: `${BASE_URL}/events/attendees`,
    getAttendeeById: (transactionId: string) =>
      `${BASE_URL}/events/booking/${transactionId}`,
    getExportAttendee: `${BASE_URL}/events/attendees/export`,
    sendTicket: (transactionId: string) =>
      `${BASE_URL}/events/booking/send-ticket/${transactionId}`,
    getEventAdminStatistics: `${BASE_URL}/event-admin/statistics`,
    generateEventReport: `${BASE_URL}/event-admin/download-report`,
    getExportAttendeeBooking: `${BASE_URL}/events/booking-attendees/export`,
    getAttendeeBookings: `${BASE_URL}/events/booking-attendees`,
  },
  sponsor: {
    getSponsorEvent: `${BASE_URL}/sponsor/events`,
    getSponsorshipDetails: (id: string) => `${BASE_URL}/sponsor/${id}`,
    getSponsorShipPlans: `${BASE_URL}/sponsor/sponsorship-plans`,
    getAdPlacementPlans: `${BASE_URL}/sponsor/ad-placement-plans`,
    sponsorShipCheckout: (id: string) => `${BASE_URL}/sponsor/checkout/${id}`,
    createSponsorEventAdvertisement: `${BASE_URL}/sponsor/event-advertisement`,
    getAdPlacementTypes: `${BASE_URL}/sponsor/ad-placement-types`,
    getAvailableADDates: `${BASE_URL}/sponsor/available-ad-dates`,
    getAdvertisementList: `${BASE_URL}/sponsor/advertisements`,
    updateAvertisementStatus: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/status/${id}`,
    getAdvertisementDetail: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/${id}`,
    editAdvertisement: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/${id}`,
    updateAdvertisementAcknowledgement: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/acknowledge/${id}`,
    updateAdvertisementPlacement: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/placement/${id}`,
    updateAdvertisementFrequency: `${BASE_URL}/sponsor/event-advertisement/frequency`,
    getAdvertisementFrequency: `${BASE_URL}/sponsor/event-advertisement/settings`,
    requestEventAdvertisementChanges: (id: string) =>
      `${BASE_URL}/sponsor/event-advertisement/request-changes/${id}`,
    getDemographicsData: `${BASE_URL}/sponsor/demographics`,
    exportDemographics: `${BASE_URL}/sponsor/export-demographics`,
    getAdvertisementStatistics: `${BASE_URL}/sponsor/demographics-statistics`,
  },
  subEvent: {
    getSubEvents: `${BASE_URL}/events/sub-events`,
    createSubEvent: `${BASE_URL}/events/sub-event`,
    getSubEventById: (id: string) => `${BASE_URL}/events/sub-event/${id}`,
    updateSubEvent: (id: string) => `${BASE_URL}/events/sub-event/${id}`,
    deleteSubEvent: (id: string) => `${BASE_URL}/events/sub-event/${id}`,
  },
}
